/**
 * Formats a number with maximum 1 decimal place
 * Examples:
 * - 3.456789 => 3.5
 * - 4.123456 => 4.1
 * - 50.0 => 50
 * - 100 => 100
 */
export const formatNumberMax1Digit = (value: number | string): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(num)) {
    return '0';
  }
  
  // Format to 1 decimal place and remove trailing zeros
  const formatted = num.toFixed(1);
  
  // Remove trailing zero and decimal point if not needed
  return formatted.replace(/\.0$/, '');
};

/**
 * Formats a currency amount with maximum 4 digits
 * @param value - The numeric value to format
 * @param currency - The currency symbol (default: 'HK$')
 * @returns Formatted currency string
 */
export const formatCurrencyMax4Digits = (value: number | string, currency: string = 'HK$'): string => {
  return `${currency}${formatNumberMax1Digit(value)}`;
};

export const formatNumberMax4Digits = (value: number | string)  => {
  return formatNumberMax1Digit(value);
}

/**
 * Formats a number for display with appropriate decimal places
 * This is a more general function that handles common currency formatting
 * @param value - The numeric value to format
 * @param maxDecimals - Maximum decimal places (default: 2)
 * @returns Formatted number string
 */
export const formatNumber = (value: number | string, maxDecimals: number = 2): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(num)) {
    return '0.00';
  }
  
  // Use toFixed but then remove trailing zeros
  return parseFloat(num.toFixed(maxDecimals)).toString();
};
